// Libraries
import { DateTime } from 'luxon';
import { useTranslation } from "react-i18next";
import { Clock } from "@untitled-ui/icons-react";
import { useState, type Dispatch, type SetStateAction } from "react";

// Components
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { FileUploadActionValue, uploadFile } from "#src/utils/uploadFile";
import { ObjectImage } from "#src/common/system/Object";
import { FeaturedIconV2 } from "#src/commonV2/FeaturedIcon";
import { UploadButtonV2 } from "#src/commonV2/UploadButton";

// API-related
import workspaceServices, {
  type IWorkspaceItem,
} from "#src/services/workspace";

interface ILogoProps {
  color: string;
  className?: string;
  currentWorkspace: IWorkspaceItem;
  allowedToUpdate: boolean;
  setColor: Dispatch<SetStateAction<string>>;
  fetchWorkspace: (workspaceId: string) => void;
}

const WorkspaceLogo = ({
  color,
  className,
  currentWorkspace,
  allowedToUpdate,
  setColor,
  fetchWorkspace
}: ILogoProps) => {
  const { t } = useTranslation("workspace", { keyPrefix: "detail" });

  const [avatarUrl, setAvatarUrl] = useState<string | null>(currentWorkspace?.logo);
  const [uploading, setUploading] = useState<boolean>(false);

  const onUpdateLogo = (files: File[]) => {
    if (currentWorkspace)
      for (const file of files) {
        const fileType = file.type.split("/")[0];
        if (fileType === "image") {
          setUploading(true);
          uploadFile(file, FileUploadActionValue.WORKSPACE_LOGO, {
            workspace_id: currentWorkspace.id,
          })
            .then((signedResponse) => {
              setAvatarUrl(signedResponse.signed_url);
              workspaceServices
                .update_workspace_logo(
                  currentWorkspace.id,
                  signedResponse.signed_url
                )
                .then(() => {
                  fetchWorkspace(currentWorkspace.id);
                  setUploading(false);
                })
                .catch((error) => {
                  setUploading(false);
                  apiErrorHandler(error, {
                    toastMessage: t("notification.updateLogo.fail"),
                  });
                });
            })
            .catch((error) => {
              setUploading(false);
              apiErrorHandler(error, {
                toastMessage: t("notification.uploadFile.fail"),
              });
            });
        }
      }
  };

  return <div className={`w-full ${className}`}>
    <div className="flex flex-col items-center gap-6">
      <div className="relative h-40 w-full">
        <div className="relative pr-4">
          <div
            style={{ backgroundColor: color }}
            className="h-24 w-full rounded-xl"
          />
          <label className="cursor-pointer absolute right-0 top-[-4px]">
            <input
              type="color"
              value={color}
              onChange={(e) => {
                setColor(e.currentTarget.value);
              }}
              className="hidden"
            />
            <FeaturedIconV2
              name="palette"
              className=""
              iconClassName="h-14 w-14"
            />
          </label>
        </div>
        <div className="absolute left-0 right-0 bottom-0 mx-auto h-fit w-fit">
          <div className="rounded-full bg-white p-4 w-full h-full border border-gray-v2-200">
            <ObjectImage
              data={avatarUrl}
              className="h-24 w-24 rounded-full bg-white object-contain"
            />
          </div>
        </div>
      </div>
      {
        allowedToUpdate && <UploadButtonV2
          className="w-full"
          accept="image/*"
          iconClassName="h-14 w-14"
          loading={uploading}
          onUpload={onUpdateLogo}
        />
      }
      <span className="flex items-center gap-1 text-sm font-regular text-gray-v2-600">
        <Clock className="h-4 w-4" />
        <span>
          {t("workspaceInfo.created")}{" "}
          {DateTime.fromSeconds(currentWorkspace.created_time).toRelative()}
        </span>
      </span>
    </div>
  </div>
};

export default WorkspaceLogo;
